'use client';

import { useTranslations } from 'next-intl';
import { useQRCode } from 'next-qrcode';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { Coupon } from '@/features/coupons/api/coupons';
import { EventService } from '@/shared/lib/event-service';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Button } from '@/shared/ui/button';
import { Layout } from '@/shared/ui/layout';
import { Media } from '@/shared/ui/media';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './coupon-modal.scss';

type Props = ModalProps & {
    coupon: Coupon;
    children?: ReactNode;
};

type CouponModalContextValue = {
    coupon: Coupon;
    title: string;
    formattedDate: string;
    onClose: ModalProps['onClose'];
};

const CouponModalContext = createContext<CouponModalContextValue | null>(null);

const useCouponModalContext = () => {
    const context = useContext(CouponModalContext);
    if (!context) {
        throw new Error('CouponModal components must be used within CouponModalWrapper.');
    }
    return context;
};

export const CouponModalWrapper = ({ coupon, children, ...props }: Props) => {
    const getLocalizedValue = useGetLocalizedValue();
    const title = getLocalizedValue(coupon.title);
    const formattedDate = new Date(coupon.validUntil).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    });

    return (
        <CouponModalContext.Provider value={{ coupon, title, formattedDate, onClose: props.onClose }}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'coupon-modal',
                    containerProps: {
                        className: 'coupon-modal__container',
                    },
                    'data-testid': 'coupon-modal',
                }}
            >
                {children}
            </Modal>
        </CouponModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). Use with prefab: "Modal" as child. */
export const CouponModalProvider = ({ coupon, children, ...props }: Props) => {
    const getLocalizedValue = useGetLocalizedValue();
    const title = getLocalizedValue(coupon.title);
    const formattedDate = new Date(coupon.validUntil).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    });

    return (
        <CouponModalContext.Provider value={{ coupon, title, formattedDate, onClose: props.onClose }}>
            {children}
        </CouponModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title" for config overrides. */
const CouponModalTitle = () => {
    const t = useTranslations();
    return <>{t('COUPON_MODAL.TITLE')}</>;
};

type ImageProps = ComponentProps<typeof Layout>;
const CouponModalImage = ({ media, ...props }: ImageProps) => {
    const { coupon, title } = useCouponModalContext();
    return (
        <Layout media={media} data-testid="coupon-modal-image" {...props}>
            <Media data={{ alt: title, url: coupon.imageUrl, unoptimized: true }} />
        </Layout>
    );
};

type NameProps = ComponentProps<typeof Layout>;
const CouponModalName = ({ media, ...props }: NameProps) => {
    const { title } = useCouponModalContext();
    return (
        <Layout element="p" media={media} data-testid="coupon-modal-name" {...props}>
            {title}
        </Layout>
    );
};

type QRPlaceholderProps = ComponentProps<typeof Layout>;
const CouponModalQRPlaceholder = ({ media, ...props }: QRPlaceholderProps) => {
    const { SVG } = useQRCode();
    const { coupon } = useCouponModalContext();
    return (
        <Layout media={media} {...props}>
            <SVG
                text={coupon.qrCodeContent}
                options={{
                    width: 188,
                    margin: 0,
                    color: {
                        dark: '#000000',
                        light: '#ffffff',
                    },
                }}
            />
        </Layout>
    );
};

type ApplyButtonProps = ComponentProps<typeof Button>;
const CouponModalApplyButton = ({ onClick, ...props }: ApplyButtonProps) => {
    const t = useTranslations();
    const { coupon, onClose } = useCouponModalContext();
    return (
        <Layout
            element={Button}
            variant="default"
            size="default"
            data-testid="coupon-modal-apply-button"
            onClick={(event) => {
                onClick?.(event);
                EventService.trigger('account:coupons:apply', { code: coupon.code });
                onClose();
            }}
            {...props}
        >
            {t('COUPON_MODAL.APPLY_BUTTON')}
        </Layout>
    );
};

type DownloadButtonProps = ComponentProps<typeof Button>;
const CouponModalDownloadButton = ({ ...props }: DownloadButtonProps) => {
    const t = useTranslations();
    return (
        <Layout
            element={Button}
            variant="secondary"
            size="default"
            data-testid="coupon-modal-download-button"
            {...props}
        >
            {t('COUPON_MODAL.DOWNLOAD_BUTTON')}
        </Layout>
    );
};

type DetailsTableProps = ComponentProps<'table'>;
const CouponModalDetailsTable = ({ ...props }: DetailsTableProps) => {
    const t = useTranslations();
    const { coupon, formattedDate } = useCouponModalContext();
    return (
        <table className="coupon-modal__details" {...props}>
            <tbody>
                <tr>
                    <td>{t('COUPON_MODAL.CODE')}</td>
                    <td align="right">
                        <b>{coupon.code}</b>
                    </td>
                </tr>
                <tr>
                    <td>{t('COUPON_MODAL.VALID')}</td>
                    <td align="right">{t('MY_COUPONS_MODAL.COUPON_CARD.UNTIL', { date: formattedDate })}</td>
                </tr>
                <tr>
                    <td>{t('COUPON_MODAL.MINIMAL_VALUE')}</td>
                    <td align="right">{coupon.minimalValue.CZK}</td>
                </tr>
            </tbody>
        </table>
    );
};

export { CouponModalWrapper as CouponModal };

export {
    CouponModalWrapper as Wrapper,
    CouponModalProvider as Provider,
    CouponModalTitle as Title,
    CouponModalImage as Image,
    CouponModalName as Name,
    CouponModalQRPlaceholder as QRPlaceholder,
    CouponModalApplyButton as ApplyButton,
    CouponModalDownloadButton as DownloadButton,
    CouponModalDetailsTable as DetailsTable,
};
